import { useQuery } from '@tanstack/react-query'
import { ConfigHttpClient, ConfigResponse } from '@omnea-app/config/configHttpClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'

type GetOrgIntegrationConfigResponseType = ConfigResponse<'/integrations/{id}/config', 'get'>
export type GetOrgIntegrationConfigData = GetOrgIntegrationConfigResponseType['data']
export type GetOrgIntegrationConfigError = GetOrgIntegrationConfigResponseType['error']
export type OrgIntegrationConfiguredEntities = NonNullable<GetOrgIntegrationConfigData>['configuredEntities']

/** Supported custom entity types from endpoint response */
export type OrgIntegrationSupportedCustomEntityTypes = NonNullable<
  NonNullable<GetOrgIntegrationConfigData>['supportedCustomEntityTypes']
>

export type OrgIntegrationAvailableEntities = NonNullable<GetOrgIntegrationConfigData>['availableEntities']
export type OrgIntegrationDepartmentSources = NonNullable<GetOrgIntegrationConfigData>['departmentSources']

export const getOrgIntegrationConfigQueryKey = (orgIntegrationId: string) => ['GetOrgIntegrationConfig', orgIntegrationId]

export const fetchOrgIntegrationConfig = async (
  orgIntegrationId: string,
  getToken: () => Promise<string>
): Promise<GetOrgIntegrationConfigData> => {
  const apiClient = new ConfigHttpClient({ authorization: await getToken() })
  const { data, error } = await apiClient.GET('/integrations/{id}/config', { params: { path: { id: orgIntegrationId } } })
  throwIfApiError(error)
  return data
}

export const useGetOrgIntegrationConfig = ({
  orgIntegrationId,
  options,
}: {
  orgIntegrationId: string
  options: { skip: boolean; shouldPoll?: boolean }
}) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery<GetOrgIntegrationConfigData, GetOrgIntegrationConfigError>({
    enabled: !options.skip,
    queryKey: getOrgIntegrationConfigQueryKey(orgIntegrationId),
    queryFn: () => fetchOrgIntegrationConfig(orgIntegrationId, getToken),
    refetchInterval: (data) => (options.shouldPoll !== false && data?.state.data ? 30000 : false), // Poll every 30 seconds
  })
}
